/**
 * AURALUX — Halo
 * Couche d'interactions additive : ne modifie aucun comportement existant
 * de Dawn (product-form.js, cart-drawer.js, animations.js restent intacts).
 * 5 effets signature, chacun désactivable indépendamment, tous respectueux
 * de prefers-reduced-motion et des écrans tactiles.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      var args = arguments;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(function () {
          fn.apply(null, args);
          ticking = false;
        });
      }
    };
  }

  /* ----------------------------------------------------------------------
     1. Halo curseur — actif uniquement au survol des zones produits / CTA.
     ---------------------------------------------------------------------- */
  function initHalo() {
    if (reduceMotion || !isFinePointer) return;

    var halo = document.createElement('div');
    halo.className = 'aurlx-halo';
    halo.setAttribute('aria-hidden', 'true');
    document.body.appendChild(halo);

    var zoneSelector =
      '.card-wrapper, .product__media-wrapper, .product__media-item, .banner, .slideshow__slide, ' +
      '.button, .product-form__submit, .quick-add__submit';

    var move = rafThrottle(function (x, y) {
      halo.style.setProperty('--aurlx-x', x - 210 + 'px');
      halo.style.setProperty('--aurlx-y', y - 210 + 'px');
    });

    document.addEventListener(
      'pointermove',
      function (evt) {
        move(evt.clientX, evt.clientY);
        var overZone = evt.target && evt.target.closest ? evt.target.closest(zoneSelector) : null;
        halo.classList.toggle('is-active', !!overZone);
      },
      { passive: true }
    );

    document.addEventListener('pointerleave', function () {
      halo.classList.remove('is-active');
    });
  }

  /* ----------------------------------------------------------------------
     2. Révélation "Aube" au scroll — indépendante du réglage natif Dawn
        (settings.animations_reveal_on_scroll / classe .scroll-trigger),
        pour ne jamais superposer deux animations sur le même élément.
     ---------------------------------------------------------------------- */
  function initReveal() {
    var selector =
      '.grid__item:not(.scroll-trigger), .banner__content:not(.scroll-trigger), .rich-text:not(.scroll-trigger)';
    var targets = document.querySelectorAll(selector);
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('aurlx-reveal', 'aurlx-in');
      });
      return;
    }

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Passe 1 — LECTURE seule : on mesure tous les éléments avant de toucher
    // au DOM, pour éviter le layout thrashing (lecture/écriture entrelacées
    // qui force un recalcul de mise en page à chaque itération).
    var measurements = [];
    targets.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      measurements.push({
        el: el,
        alreadyVisible: rect.top < viewportHeight && rect.bottom > 0
      });
    });

    // Passe 2 — ÉCRITURE seule : toutes les lectures sont terminées, on peut
    // maintenant modifier les classes/styles sans provoquer de reflow forcé.
    var toObserve = [];
    measurements.forEach(function (item) {
      var el = item.el;
      el.classList.add('aurlx-reveal');

      var parent = el.parentElement;
      if (parent) {
        parent.__aurlxCount = parent.__aurlxCount || 0;
        el.style.setProperty('--aurlx-order', parent.__aurlxCount % 6);
        parent.__aurlxCount++;
      }

      // Évite le flash : les éléments déjà visibles à l'écran au chargement
      // n'attendent pas l'observer, ils sont révélés immédiatement.
      if (item.alreadyVisible) {
        el.classList.add('aurlx-in');
      } else {
        toObserve.push(el);
      }
    });

    if (!toObserve.length) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('aurlx-in');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    toObserve.forEach(function (el) {
      observer.observe(el);
    });

    // Compatibilité éditeur de thème : révèle les nouvelles sections ajoutées en live.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (evt) {
        var newTargets = evt.target.querySelectorAll(selector);
        newTargets.forEach(function (el) {
          el.classList.add('aurlx-reveal', 'aurlx-in');
        });
      });
    }
  }

  /* ----------------------------------------------------------------------
     3. Fondu-lumière produit — marque les conteneurs média comme cibles
        du glow ::after défini en CSS (vient compléter le hover natif Dawn).
     ---------------------------------------------------------------------- */
  function initGlow() {
    document.querySelectorAll('.card__media, .product__media-wrapper').forEach(function (el) {
      el.classList.add('aurlx-glow-target');
    });
  }

  /* ----------------------------------------------------------------------
     4. "Lumière allumée" — pulsation à la confirmation d'ajout panier.
        S'appuie sur le pub/sub natif de Dawn (cart-update), sans toucher
        à product-form.js ni à la logique panier.
     ---------------------------------------------------------------------- */
  function initCartConfirmation() {
    if (reduceMotion) return;

    var lastButton = null;

    document.addEventListener(
      'submit',
      function (evt) {
        var form = evt.target;
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        lastButton = form.querySelector('[type="submit"]');
      },
      true
    );

    function pulse(el) {
      if (!el) return;
      el.classList.remove('aurlx-lit');
      void el.offsetWidth; // force reflow pour pouvoir rejouer l'animation
      el.classList.add('aurlx-lit');
      window.setTimeout(function () {
        el.classList.remove('aurlx-lit');
      }, 950);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        pulse(lastButton);
        var bubble = document.querySelector('.cart-count-bubble') || document.getElementById('cart-icon-bubble');
        pulse(bubble);
      });
    }
  }

  /* ----------------------------------------------------------------------
     5. Fil d'Aura — barre de progression de scroll.
     ---------------------------------------------------------------------- */
  function initAura() {
    if (reduceMotion) return;

    var bar = document.createElement('div');
    bar.className = 'aurlx-aura-bar';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    var update = rafThrottle(function () {
      var doc = document.documentElement;
      var scrollTop = doc.scrollTop || document.body.scrollTop;
      var height = doc.scrollHeight - doc.clientHeight || 1;
      var ratio = Math.min(Math.max(scrollTop / height, 0), 1);
      bar.style.transform = 'scaleX(' + ratio + ')';
    });

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  ready(function () {
    initHalo();
    initReveal();
    initGlow();
    initCartConfirmation();
    initAura();
  });
})();
